import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import AlertModal from './AlertModal';

const Navbar = ({ onLoginClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState('');
  const [targetPath, setTargetPath] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogin = () => {
    if (onLoginClick) {
      onLoginClick();
    }
  };

  const handleFeatureClick = (featureName, path) => {
    if (isAuthenticated) {
      if (path === '/resume-generator') {
        navigate('/app/resume-generator');
        return;
      }
      navigate(path);
    } else {
      setSelectedFeature(featureName);
      setTargetPath(path);
      setShowLoginPrompt(true);
    }
  };

  const handleConfirmLogin = () => {
    setShowLoginPrompt(false);
    if (onLoginClick) {
      onLoginClick();
    }
  };

  const handleHomeClick = () => {
    navigate('/app');
  };

  const handleBrandClick = (e) => {
    e.preventDefault();
    // 如果当前在首页，则刷新页面
    if (window.location.pathname === '/') {
      window.location.reload();
    } else {
      // 如果不在首页，则导航到首页
      navigate('/');
    }
  };

  return (
    <>
      <NavContainer>
        <NavContent>
          <LeftGroup>
            <Brand onClick={handleBrandClick}>
              <BrandIcon src="/images/logos/logo.svg" alt="职唛 AI Logo" />
              <BrandText>职唛 AI</BrandText>
            </Brand>
          </LeftGroup>

          <DesktopNav>
            <NavButton onClick={() => handleFeatureClick('简历生成', '/resume-generator')}>简历生成</NavButton>
            <NavButton onClick={() => handleFeatureClick('简历诊断', '/app/resumes/diagnosis')}>简历诊断</NavButton>
            <NavButton onClick={() => handleFeatureClick('免费模板', '/app/resume-templates')}>免费模板</NavButton>
            <NavButton onClick={() => handleFeatureClick('面试押题', '/app/interviews/prep')}>面试押题</NavButton>
            <NavButton onClick={() => handleFeatureClick('模拟面试', '/app/interviews/mock')}>模拟面试</NavButton>
            <NavButton onClick={() => handleFeatureClick('面试助手', '/app/meetings')}>面试助手</NavButton>
          </DesktopNav>

          <UserActions>
            {isAuthenticated ? (
              <LoginButton onClick={handleHomeClick}>主页</LoginButton>
            ) : (
              <LoginButton onClick={handleLogin}>登录/注册</LoginButton>
            )}
          </UserActions>

          <MobileMenuButton onClick={toggleMenu}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </MobileMenuButton>
        </NavContent>

        <MobileNav isOpen={isMenuOpen}>
          <MobileNavButton onClick={() => handleFeatureClick('简历生成', '/resume-generator')}>简历生成</MobileNavButton>
          <MobileNavButton onClick={() => handleFeatureClick('简历诊断', '/app/resumes/diagnosis')}>简历诊断</MobileNavButton>
          <MobileNavButton onClick={() => handleFeatureClick('免费模板', '/app/resume-templates')}>免费模板</MobileNavButton>
          <MobileNavButton onClick={() => handleFeatureClick('面试押题', '/app/interviews/prep')}>面试押题</MobileNavButton>
          <MobileNavButton onClick={() => handleFeatureClick('模拟面试', '/app/interviews/mock')}>模拟面试</MobileNavButton>
          <MobileNavButton onClick={() => handleFeatureClick('面试助手', '/app/meetings')}>面试助手</MobileNavButton>
          <MobileUserActions>
            {isAuthenticated ? (
              <LoginButton onClick={handleHomeClick}>主页</LoginButton>
            ) : (
              <LoginButton onClick={handleLogin}>登录/注册</LoginButton>
            )}
          </MobileUserActions>
        </MobileNav>
      </NavContainer>

      <AlertModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="解锁专属 AI 助手"
        message={`即将为您开启「${selectedFeature}」。\n\n为确保您的数据安全并提供连贯的使用体验，请先登录或注册职唛 AI。\n\n只需几秒，即可体验高效的求职服务。`}
        type="ai"
        onConfirm={handleConfirmLogin}
        confirmText="立即登录/注册"
        showCancel={false}
        cancelText="暂不体验"
      />
    </>
  );
};

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  z-index: 1000;
`;

const NavContent = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 1920px) {
    max-width: 1600px;
    padding: 1.25rem 3rem;
  }

  @media (min-width: 2560px) {
    max-width: 1800px;
    padding: 1.5rem 4rem;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (min-width: 1920px) {
    gap: 12px;
  }

  @media (min-width: 2560px) {
    gap: 14px;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: clamp(1.25rem, 1.5vw, 1.75rem);
  color: #1a1a1a;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  @media (min-width: 1920px) {
    font-size: clamp(1.5rem, 1.8vw, 2rem);
    gap: 0.75rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.75rem, 2vw, 2.25rem);
    gap: 1rem;
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const BrandIcon = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 8px;
  transition: all 0.2s ease;

  @media (min-width: 1920px) {
    width: 40px;
    height: 40px;
  }

  @media (min-width: 2560px) {
    width: 48px;
    height: 48px;
  }
`;

const BrandText = styled.span`
  color: #2c3e50;
`;


const DesktopNav = styled.div`
  display: flex;
  gap: 1.5rem;

  @media (min-width: 1920px) {
    gap: 2rem;
  }

  @media (min-width: 2560px) {
    gap: 2.5rem;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;



const NavButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #374151;
  padding: 0.5rem 0.875rem;
  font-size: clamp(0.9rem, 1vw, 1.1rem);
  font-family: inherit;
  user-select: none;
  transition: all 0.2s ease;
  border-radius: 8px;

  &:hover {
    color: #6366F1;
    background: rgba(99, 102, 241, 0.05);
  }

  @media (min-width: 1920px) {
    font-size: clamp(1rem, 1.2vw, 1.2rem);
    padding: 0.625rem 1.125rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.1rem, 1.3vw, 1.3rem);
    padding: 0.75rem 1.375rem;
  }
`;

const UserActions = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const LoginButton = styled.button`
  background: linear-gradient(45deg, #6366F1, #8B5CF6);
  border: none;
  color: white;
  padding: clamp(0.5rem, 0.8vw, 0.75rem) clamp(1rem, 1.4vw, 1.5rem);
  border-radius: 25px;
  cursor: pointer;
  transition: transform 0.2s;
  font-size: clamp(0.9rem, 1vw, 1.1rem);
  font-weight: 600;

  @media (min-width: 1920px) {
    padding: 0.75rem 1.6rem;
    font-size: clamp(1rem, 1.2vw, 1.2rem);
    border-radius: 30px;
  }

  @media (min-width: 2560px) {
    padding: 0.875rem 1.9rem;
    font-size: clamp(1.1rem, 1.3vw, 1.3rem);
    border-radius: 35px;
  }

  &:hover {
    transform: translateY(-1px);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #374151;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileNav = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    flex-direction: column;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    padding: 1rem 2rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }
`;



const MobileNavButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #374151;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 1rem;
  font-family: inherit;
  user-select: none;
  width: 100%;
  text-align: left;
  transition: color 0.2s ease;

  &:hover {
    color: #6366F1;
  }
`;

const MobileUserActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

export default Navbar;
